import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";

export const POST_TYPES = [
  { key: "win",      label: "Win",      emoji: "🏆", color: "#e0f2fe", border: "#7dd3fc" },
  { key: "question", label: "Question", emoji: "❓", color: "#fff7ed", border: "#fed7aa" },
  { key: "checkin",  label: "Check-in", emoji: "📸", color: "#f0fdf4", border: "#bbf7d0" },
  { key: "general",  label: "General",  emoji: "💬", color: "#f8fbff", border: "#bfdbfe" },
];

// ── Posts ─────────────────────────────────────────────────────────────────────

export async function getCommunityPosts(programTag) {
  const snap = await getDocs(
    query(
      collection(db, "communityPosts"),
      where("programTag", "==", programTag)
    )
  );
  const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Sort: unanswered questions first, then newest first
  return posts.sort((a, b) => {
    const aUnanswered = a.type === "question" && !a.isAnswered;
    const bUnanswered = b.type === "question" && !b.isAnswered;
    if (aUnanswered && !bUnanswered) return -1;
    if (!aUnanswered && bUnanswered) return 1;
    const aTime = a.createdAt?.toDate?.()?.getTime?.() || 0;
    const bTime = b.createdAt?.toDate?.()?.getTime?.() || 0;
    return bTime - aTime;
  });
}

export async function createPost({ programTag, type, text, photos, currentUser }) {
  return await addDoc(collection(db, "communityPosts"), {
    programTag,
    type,
    text: text.trim(),
    photos: photos || [],
    authorId: currentUser.uid,
    authorName: currentUser.name || "",
    authorAvatar: currentUser.avatar || "",
    isAnswered: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function createPostWithMentions({
  programTag,
  type,
  text,
  photos,
  currentUser,
  mentionUserIds = [],
  mentionAll = false,
}) {
  return await addDoc(collection(db, "communityPosts"), {
    programTag,
    type,
    text: text.trim(),
    photos: photos || [],
    authorId: currentUser.uid,
    authorName: currentUser.name || "",
    authorAvatar: currentUser.avatar || "",
    mentionUserIds,
    mentionAll,
    isAnswered: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function markQuestionAnswered(postId, answered) {
  await updateDoc(doc(db, "communityPosts", postId), { isAnswered: answered });
}

export async function updatePost(postId, { text, type }) {
  await updateDoc(doc(db, "communityPosts", postId), {
    text: text.trim(),
    type,
    updatedAt: serverTimestamp(),
    editedAt: serverTimestamp(),
  });
}

export async function deletePost(postId) {
  await deleteDoc(doc(db, "communityPosts", postId));
}

export async function uploadPostPhoto(uid, postId, file) {
  const photoId = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const path = `communityPhotos/${uid}/${postId}/${photoId}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { id: photoId, url, path };
}

export async function deletePostPhoto(path) {
  try {
    await deleteObject(ref(storage, path));
  } catch {}
}

// ── Comments ──────────────────────────────────────────────────────────────────

export async function getCommentsForPost(postId) {
  const snap = await getDocs(
    query(collection(db, "communityComments"), where("postId", "==", postId))
  );
  const comments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return comments.sort((a, b) => {
    const aTime = a.createdAt?.toDate?.()?.getTime?.() || 0;
    const bTime = b.createdAt?.toDate?.()?.getTime?.() || 0;
    return aTime - bTime;
  });
}

export async function addComment({ postId, text, currentUser }) {
  await addDoc(collection(db, "communityComments"), {
    postId,
    text: text.trim(),
    authorId: currentUser.uid,
    authorName: currentUser.name || "",
    authorAvatar: currentUser.avatar || "",
    createdAt: serverTimestamp(),
  });
}

export async function addCommentWithMentions({
  postId,
  text,
  currentUser,
  mentionUserIds = [],
  mentionAll = false,
}) {
  await addDoc(collection(db, "communityComments"), {
    postId,
    text: text.trim(),
    authorId: currentUser.uid,
    authorName: currentUser.name || "",
    authorAvatar: currentUser.avatar || "",
    mentionUserIds,
    mentionAll,
    createdAt: serverTimestamp(),
  });
}

export async function getCommunityMembers(programTag) {
  if (!programTag) return [];
  const snap = await getDocs(
    query(collection(db, "users"), where("programTag", "==", programTag))
  );
  return snap.docs.map((d) => {
    const data = d.data() || {};
    return {
      id: d.id,
      name: data.name || "User",
      avatar: data.avatar || "",
    };
  });
}

export async function deleteComment(commentId) {
  await deleteDoc(doc(db, "communityComments", commentId));
}

// ── Reactions ─────────────────────────────────────────────────────────────────

export async function getReactionsForPost(postId) {
  const snap = await getDocs(
    query(collection(db, "communityReactions"), where("postId", "==", postId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function togglePostReaction({ postId, emoji, currentUser }) {
  const reactionId = `${postId}_${emoji}_${currentUser.uid}`;
  const reactionRef = doc(db, "communityReactions", reactionId);
  const snap = await getDoc(reactionRef);
  if (snap.exists()) {
    await deleteDoc(reactionRef);
  } else {
    await setDoc(reactionRef, {
      postId,
      emoji,
      userId: currentUser.uid,
      userName: currentUser.name || "",
      createdAt: serverTimestamp(),
    });
  }
}

// ── Comment Reactions ─────────────────────────────────────────────────────────

export async function getReactionsForComment(commentId) {
  const snap = await getDocs(
    query(collection(db, "communityCommentReactions"), where("commentId", "==", commentId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function toggleCommentReaction({ commentId, emoji, currentUser }) {
  const reactionId = `${commentId}_${emoji}_${currentUser.uid}`;
  const reactionRef = doc(db, "communityCommentReactions", reactionId);
  const snap = await getDoc(reactionRef);
  if (snap.exists()) {
    await deleteDoc(reactionRef);
  } else {
    await setDoc(reactionRef, {
      commentId,
      emoji,
      userId: currentUser.uid,
      userName: currentUser.name || "",
      createdAt: serverTimestamp(),
    });
  }
}
